import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, Switch } from '@mui/material';
import { toImgSrcKey } from '../../../utils/cdn';
import { formatWeight } from '../utils/formatters';
import { updateProductAvailability } from '../../../api';
import s from '../styles/ProductCard.module.scss';

function resolveProductImage(product) {
    const candidate =
        product?.image ||
        product?.imageKey ||
        (Array.isArray(product?.images) ? product.images[0] : null) ||
        null;

    if (!candidate) return '';
    if (typeof candidate === 'string') return toImgSrcKey(candidate);
    return toImgSrcKey(candidate.key || candidate.url || '');
}

function toBrandRoute(brandId) {
    const safeId = String(brandId ?? '');
    return safeId ? `/brands/${encodeURIComponent(safeId)}` : '/brands';
}

export default function ProductCard({
    product,
    brand,
    showSelection = false,
    selected = false,
    onSelect,
    reorderSlot = null,
    actionsSlot = null,
    onKeyDown,
}) {
    const [available, setAvailable] = useState(Boolean(product.isAvailable));
    const [busy, setBusy] = useState(false);

    const brandName = product.brand || brand?.name || 'No brand';
    const displayName = product.name || 'Untitled product';
    const imageSrc = resolveProductImage(product);
    const brandRoute = toBrandRoute(brand?.id || product.brandId);

    async function onToggleAvailability() {
        if (busy) return;
        const next = !available;
        setAvailable(next);
        setBusy(true);

        try {
            await updateProductAvailability(product.id, next);
        } catch (e) {
            setAvailable(!next);
            // Keep this lightweight to avoid global state coupling in shared card component.
            alert(e?.message || 'Failed to update availability');
        } finally {
            setBusy(false);
        }
    }

    return (
        <article
            className={s.card}
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-label={`${displayName} product card`}
        >
            <div className={s.leftRail}>
                {showSelection && (
                    <Checkbox
                        checked={selected}
                        onChange={(e) => onSelect?.(e.target.checked)}
                        inputProps={{ 'aria-label': `Select ${displayName}` }}
                    />
                )}
                {reorderSlot}
            </div>

            <div className={s.imageCol}>
                {imageSrc ? (
                    <img className={s.image} src={imageSrc} alt={displayName} loading="lazy" />
                ) : (
                    <div className={s.imagePlaceholder}>No image</div>
                )}
            </div>

            <div className={s.mainCol}>
                <strong className={s.title}>{displayName}</strong>
                <div className={s.metaRow}>
                    <Link to={brandRoute} className={s.linkText}>
                        {brandName}
                    </Link>
                    <span className={s.dot}>|</span>
                    <span>{product.flavor || 'No flavor'}</span>
                    <span className={s.dot}>|</span>
                    <span>{formatWeight(product)}</span>
                </div>
            </div>

            <div className={s.rightCol}>
                <div className={s.availability}>
                    <Switch
                        size="small"
                        checked={available}
                        onChange={onToggleAvailability}
                        disabled={busy}
                        inputProps={{ 'aria-label': `Toggle availability for ${displayName}` }}
                    />
                </div>
                <div className={s.actions}>{actionsSlot}</div>
            </div>
        </article>
    );
}
